import React from 'react';
import { getLessonPublicCoverImageUrl, type Lesson } from '@court-sync/shared';
import type { AcademySport } from '@court-sync/shared';
import { getLessonCoverPlaceholderStyle } from '../../utils/lessonCoverPlaceholder';
import { BrowserImage } from '../ui/BrowserImage';
import { SportGlyph } from '@/components/sport/SportGlyph';
import { resolveLessonSport } from '@/lib/sportPresentation';

export type PublicLessonCardImageProps = {
  lesson: Pick<Lesson, 'imageUrl' | 'programImageUrl' | 'name' | 'academySportId'>;
  /** Override display name for placeholder monogram (e.g. camp group name). */
  placeholderName?: string;
  /** Academy primary color for placeholder gradient blend. */
  accentColor?: string | null;
  academySports?: AcademySport[] | null;
  className?: string;
};

const defaultContainerClass =
  'w-full overflow-hidden border-b border-border bg-muted aspect-[4/3] sm:aspect-video';

/**
 * Cover for public lesson cards: lesson image, program fallback, or branded placeholder.
 */
export function PublicLessonCardImage({
  lesson,
  placeholderName,
  accentColor,
  academySports,
  className,
}: PublicLessonCardImageProps) {
  const src = getLessonPublicCoverImageUrl(lesson);
  const containerClass = className ?? defaultContainerClass;
  const displayName = placeholderName ?? lesson.name;
  const { monogram, gradientFrom, gradientTo } = getLessonCoverPlaceholderStyle(
    displayName,
    accentColor
  );
  const lessonSport = resolveLessonSport(lesson, academySports);

  const placeholder = (
    <div
      className={containerClass}
      style={{
        background: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
      }}
      aria-hidden
    >
      <div className="relative flex h-full w-full flex-col items-center justify-center gap-2">
        <SportGlyph
          sportId={lessonSport?.sportId ?? 'tennis'}
          variant="light"
          className="absolute right-4 top-4 h-8 w-8 opacity-25"
        />
        <span className="select-none text-3xl font-bold tracking-tight text-white/95 drop-shadow-sm sm:text-4xl">
          {monogram}
        </span>
      </div>
    </div>
  );

  if (src) {
    return (
      <div className={containerClass}>
        <BrowserImage
          src={src}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
          fallback={placeholder}
        />
      </div>
    );
  }

  return placeholder;
}
